import { DateFormatter4 } from "../../../../Constants";
import styles from "../TrackDetails.module.css";
import { useSimConsent } from "../../hooks/useSimConsent";
import SimConsentStatus from "../../components/indentTracking/SimConsentStatus";
import { useFetchSimTracking } from "../hooks/useFetchSimTracking";

const RightSideBar = ({ data, err, height, indentId, activeTab }) => {
  const {
    isSendingRequest,
    isCheckingStatus,
    sendConsentRequest,
    parseConsentStatus,
    vehicleDetails: simConsentDetails,
    refetchSimTrackingStatus,
    loading,
    hasAccess,
  } = useSimConsent(indentId);

  const restrictSimTracking = (data?.status?.length > 0 && ["yet to be placed", "placed", "dispatched"].includes(data?.status?.[data?.status?.length - 1]?.value)) ? false : true;

  const consentDetails = simConsentDetails || {};
  const simConsentStatus = parseConsentStatus(consentDetails);

  const DateFormatter7 = (inputdate) => {
    let date;

    // Check if the input date format is Fastag ("YYYY-MM-DD HH:MM:SS")
    if (inputdate.includes("-") && inputdate.includes(":") && inputdate.indexOf("-") < inputdate.indexOf(":")) {
      const [dateString, time] = inputdate.split(" ");
      const [year, month, day] = dateString.split("-");
      const [hour, minute, second] = time.split(":");

      const amOrPm = parseInt(hour, 10) >= 12 ? "PM" : "AM";
      let hours = parseInt(hour, 10);
      if (hours > 12) hours -= 12;
      if (hours === 0) hours = 12;

      return `${day}-${month}-${year}, ${hours}:${minute} ${amOrPm}`;
    } else if (inputdate.includes(":") && inputdate.includes("-")) {
      // SIM tracking format: "10:45:14 05-03-2026"
      const [time, dateString] = inputdate.split(" ");
      const [hour, minute, second] = time.split(":");
      const [day, month, year] = dateString.split("-");

      const amOrPm = parseInt(hour, 10) >= 12 ? "PM" : "AM";
      let hours = parseInt(hour, 10);
      if (hours > 12) hours -= 12;
      if (hours === 0) hours = 12; // Handle midnight case

      return `${day}-${month}-${year}, ${hours}:${minute} ${amOrPm}`;
    } else {
      // for ISO formats
      date = new Date(inputdate);
      if (isNaN(date)) return;

      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      const time = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });

      return `${day}-${month}-${year}, ${time}`;
    }
  };

  return (
    <div className="col-12 col-xl-3">
      <div
        className="card shadow-lg border-0"
        style={{
          borderRadius: "0.75rem",
          overflow: "hidden",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
        }}
      >
        {/* Header */}
        <div
          className="card-header d-flex align-items-center justify-content-between text-white"
          style={{
            background: "linear-gradient(135deg, #00b4d8)",
            borderBottom: "none",
            padding: "0.75rem 1.25rem",
          }}
        >
          <h3 className="h5 mb-0 font-weight-bold d-flex align-items-center">
            <i className="fa fa-signal mr-2" aria-hidden="true"></i>
            Last Known Location
          </h3>
        </div>

        {/* Body */}
        {!err ? (
          <div
            className="card-body"
            style={{
              maxHeight: height,
              overflowY: "auto",
              padding: "1rem 1.25rem",
            }}
          >
            {/* SIM Consent Status - Show if no updates or very few updates (just origin/dest placeholders) */
              /* We check if we are in SIM mode (implied by indentId prop existence or we could pass a prop) */
              /* But simpler: if statusUpdates has only 2 items (Origin & Dest) and keys 'kind' are origin/dest, then likely no pings */
              (hasAccess && activeTab === "sim" && data.statusUpdates?.length <= 2 && indentId) && (
                <div className="mb-3">
                  <SimConsentStatus
                    restrictSimTracking={restrictSimTracking}
                    consentStatus={simConsentStatus}
                    loading={false}
                    isSendingRequest={isSendingRequest}
                    isCheckingStatus={isCheckingStatus}
                    onSendRequest={sendConsentRequest}
                    onCheckStatus={refetchSimTrackingStatus}
                    showInfoBox={false}
                  />
                </div>
              )}

            {data.statusUpdates?.length > 0 ? (
              <ul className="list-unstyled m-0">
                {data.statusUpdates?.map((u, i) => (
                  <li
                    key={i}
                    className={`d-flex position-relative 
                      ${data?.status?.length >= 4 ? styles.pb : i === 0 ? styles.cb : styles.pb}
                    `}
                  >
                    {/* Timeline connector */}
                    {i < data.statusUpdates.length - 1 && (
                      <div
                        className="position-absolute"
                        style={{
                          left: "8px",
                          top: "14px",
                          bottom: 0,
                          width: "2px",
                          borderLeft:
                            i === 0
                              ? "2px dotted gray"
                              : "2px solid rgba(25,135,84,0.9)",
                        }}
                      />
                    )}

                    {/* Timeline icon */}
                    <div
                      className={`rounded-circle flex-shrink-0 mr-3 position-relative text-white
                      ${u.kind === "origin"
                          ? "bg-success"
                          : u.kind === "destination"
                            ? "bg-danger"
                            : i === 1 && data?.status?.length < 4
                              ? `${styles.pulse} bg-primary`
                              : "bg-secondary"
                        }`}
                      // pulse
                      style={{
                        width: "18px",
                        height: "18px",
                        boxShadow:
                          u.kind === "ping" && i === 1 && data?.status?.length < 4
                            ? "0 0 6px rgba(0,123,255,0.6)"
                            : "none",
                      }}
                    >
                      <i
                        className={`fa ${u.kind === "origin"
                          ? "fa-flag"
                          : u.kind === "destination"
                            ? "fa-map-marker"
                            : i === 1 && u.kind === "ping" && data?.status?.length < 4
                              ? "fa-truck"
                              : "fa-circle"
                          }`}
                        style={{
                          fontSize:
                            u.kind === "destination" ||
                              (i === 1 && u.kind === "ping")
                              ? "8px"
                              : "8px",
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                        }}
                      />
                    </div>

                    {/* Timeline text */}
                    <div className="ml-2">
                      <div className="font-weight-semibold">
                        {i === 0 || i === data.statusUpdates?.length - 1 ? (
                          u?.status?.label
                        ) : (
                          <div style={{ fontSize: "13px" }}>{u?.status}</div>
                        )}{" "}
                        {u.kind === "origin" && (
                          <span className="badge bg-success ms-1">Origin</span>
                        )}
                        {u.kind === "destination" && (
                          <span className="badge bg-danger ms-1">
                            Destination
                          </span>
                        )}
                      </div>
                      {u.timestamp && (
                        <div className="d-flex align-content-center">
                          <div
                            className={`small text-muted ${i === data.statusUpdates?.length - 1 && "mt-2"
                              }`}
                          >
                            {DateFormatter7(u?.timestamp) ?? u.timestamp}
                          </div>
                          {data?.status?.length >= 4 &&
                            u.kind === "destination" && (
                              <div className="small text-muted ml-3">
                                Reached Successfully ✅
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-secondary text-center py-4">
                No updates available.
              </div>
            )}
          </div>
        ) : (
          (hasAccess && activeTab === "sim") && (err === "No SIM tracking details found" || err?.includes("SIM tracking")) ? (
            <div className="p-3 mb-3">
              <SimConsentStatus
                restrictSimTracking={restrictSimTracking}
                consentStatus={simConsentStatus}
                loading={false}
                isSendingRequest={isSendingRequest}
                isCheckingStatus={isCheckingStatus}
                onSendRequest={sendConsentRequest}
                onCheckStatus={refetchSimTrackingStatus}
                showInfoBox={false}
              />
            </div>
          ) : (
            <div
              className="d-flex flex-column justify-content-center align-items-center text-muted p-4 text-center"
              style={{ minHeight: "280px" }}
            >
              <i className="fa fa-exclamation-circle fa-2x mb-3 text-secondary" />
              <div className="font-weight-semibold">
                Live Tracking Not Available For This Vehicle
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default RightSideBar;
